import type { RouteLocationRaw } from 'vue-router';
import { entityTypeFromTaxId, normalizeCpf, normalizeCnpj } from './formatters';

// The per-entity sections that exist for both person and company. Most follow the
// `${person|company}-${section}` route-name convention (e.g. `person-kyt`, `person-alerts`);
// the irregular names are handled in `entityRouteName` below.
export type EntitySection = 'kyt' | 'kyc' | 'alerts' | 'sar' | 'investigations' | 'entity-pulse';

type EntityType = 'company' | 'person';

// The route NAME for an entity type + section. With no section, links to the entity's home page.
// A couple of names don't follow the plain `${type}-${section}` pattern, so they're mapped here.
function entityRouteName(type: EntityType, section?: EntitySection): string {
  if (!section) return type === 'person' ? 'person-general-data' : 'company-general-headquarters';
  if (type === 'company' && section === 'kyc') return 'company-kyc-lookup';
  return `${type}-${section}`;
}

/**
 * Route to an entity's own page, inferred from its tax id. A CPF (11 digits) → the person route
 * keyed by `uuid`; a CNPJ → the company route keyed by the 8-digit `cnpj` root (the `:cnpj` param
 * accepts full or root). With no `section` it targets the entity's home page.
 *
 * `opts.type` overrides the tax-id-based type inference (for masked/partial ids); `opts.personUuid`
 * supplies the person route key directly when the CPF isn't it. Returns null when the id can't be
 * resolved to a valid route.
 */
export function entityRouteForTaxId(
  taxId: string | null | undefined,
  section?: EntitySection,
  opts?: { type?: EntityType; personUuid?: string | null },
): RouteLocationRaw | null {
  if (!taxId) return null;
  const type = opts?.type ?? entityTypeFromTaxId(taxId);

  if (type === 'person') {
    const uuid = opts?.personUuid || normalizeCpf(taxId);
    if (!uuid || (!opts?.personUuid && uuid.length !== 11)) return null;
    return { name: entityRouteName('person', section), params: { uuid } };
  }

  const cnpj = normalizeCnpj(taxId).slice(0, 8);
  return cnpj.length >= 8 ? { name: entityRouteName('company', section), params: { cnpj } } : null;
}
